import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Music, Send, ExternalLink, Lock, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

const PLATFORM_LABELS = {
  spotify: "Spotify",
  apple_music: "Apple Music",
  tidal: "Tidal",
  youtube_music: "YouTube Music",
  amazon_music: "Amazon Music",
  deezer: "Deezer",
};

const STATUS_META = {
  submitted: { color: "#FFD700", icon: Clock, label: "Submitted" },
  in_review: { color: "#FF6600", icon: Clock, label: "In Review" },
  live: { color: "#00FF00", icon: CheckCircle, label: "Live" },
  rejected: { color: "#FF0000", icon: Lock, label: "Rejected" },
};

export default function DistributionHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProArtist = user?.active_plans?.includes("artist_pro");

  const [tracks, setTracks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    const [pRes, tRes, sRes] = await Promise.all([
      fetch(`${API}/distribution/platforms`),
      fetch(`${API}/tracks?user_id=${user.user_id}`, { credentials: "include" }).catch(() => null),
      isProArtist ? fetch(`${API}/distribution/submissions`, { credentials: "include" }) : Promise.resolve(null),
    ]);
    if (pRes.ok) setPlatforms((await pRes.json()).platforms);
    if (tRes && tRes.ok) setTracks(await tRes.json());
    if (sRes && sRes.ok) setSubmissions(await sRes.json());
  }, [user, isProArtist]);

  useEffect(() => { load(); }, [load]);

  const togglePlatform = (p) => {
    setSelectedPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  const submit = async () => {
    if (!selectedTrack) { toast.error("Pick a track"); return; }
    if (selectedPlatforms.length === 0) { toast.error("Pick at least one platform"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/distribution/submit`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ track_id: selectedTrack, platforms: selectedPlatforms }),
      });
      if (r.status === 402) { toast.error("Artist Pro required"); navigate("/plans"); return; }
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      toast.success("Track submitted for distribution!");
      setSelectedTrack(""); setSelectedPlatforms([]);
      load();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  if (!isProArtist) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
        <Header />
        <main className="flex-1 container mx-auto px-4 py-10 max-w-2xl text-center">
          <Lock className="w-16 h-16 mx-auto text-[#FF6600] mb-4" />
          <h1 className="text-3xl font-bold text-[#FF6600] mb-2" data-testid="distribution-locked-title">Distribution Hub</h1>
          <p className="text-[#FF66FF] mb-6">Subscribe to <strong>Artist Pro</strong> to distribute your music to Spotify, Apple Music, Tidal, YouTube Music, Amazon Music, and Deezer.</p>
          <button onClick={() => navigate("/plans")} className="myspace-btn-primary" data-testid="distribution-upgrade-btn">
            Get Artist Pro — $30/30 days
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-3xl">
        <div className="text-center mb-6">
          <Music className="w-14 h-14 mx-auto text-[#00FFFF] mb-2" style={{ filter: "drop-shadow(0 0 15px #00FFFF)" }} />
          <h1 className="text-3xl font-bold text-[#00FFFF]" style={{ textShadow: "0 0 10px #00FFFF" }} data-testid="distribution-title">Distribution Hub</h1>
          <p className="text-[#FF66FF] mt-1">Get your music on every major streaming platform</p>
        </div>

        {/* Submit form */}
        <div className="myspace-card mb-6">
          <h2 className="text-lg font-bold text-[#FFD700] mb-3">Submit a track</h2>
          <label className="block text-xs text-[#99CCFF] mb-1">Pick a track</label>
          <select
            value={selectedTrack}
            onChange={(e) => setSelectedTrack(e.target.value)}
            className="w-full bg-[rgba(0,0,0,0.4)] border-2 border-[#6600CC] text-[#00FFFF] px-3 py-2 mb-4 outline-none focus:border-[#FF00FF]"
            data-testid="distribution-track-select"
          >
            <option value="">— Choose a track —</option>
            {tracks.map((t) => (
              <option key={t.track_id} value={t.track_id}>{t.title}</option>
            ))}
          </select>

          <p className="text-xs text-[#99CCFF] mb-2">Pick platforms</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {platforms.map((p) => (
              <button
                key={p}
                onClick={() => togglePlatform(p)}
                className={`p-2 border-2 text-sm transition ${selectedPlatforms.includes(p) ? "border-[#00FF00] bg-[rgba(0,255,0,0.1)] text-[#00FF00]" : "border-[#6600CC] text-[#FF66FF] hover:border-[#FF00FF]"}`}
                data-testid={`dist-platform-${p}`}
              >
                {PLATFORM_LABELS[p] || p}
              </button>
            ))}
          </div>

          <button
            onClick={submit}
            disabled={submitting || !selectedTrack || selectedPlatforms.length === 0}
            className="myspace-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="distribution-submit-btn"
          >
            <Send className="w-4 h-4" /> {submitting ? "Submitting..." : "Submit for Distribution"}
          </button>
          {tracks.length === 0 && (
            <p className="text-xs text-[#FF6600] mt-3 text-center">
              You don't have any tracks yet. Upload one in the Artist Hub first.
            </p>
          )}
        </div>

        {/* Submissions list */}
        <h2 className="text-xl font-bold text-[#FF00FF] mb-3">Your submissions</h2>
        {submissions.length === 0 ? (
          <p className="text-[#99CCFF] text-sm text-center py-6">No submissions yet.</p>
        ) : (
          <div className="space-y-2" data-testid="submissions-list">
            {submissions.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.submitted;
              const StatusIcon = meta.icon;
              return (
                <div key={s.submission_id} className="myspace-card flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`submission-${s.submission_id}`}>
                  <div className="flex-1">
                    <p className="text-[#00FFFF] font-bold">{s.track_title}</p>
                    <p className="text-xs text-[#99CCFF]">{s.platforms.map((p) => PLATFORM_LABELS[p] || p).join(", ")}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-bold border px-2 py-1" style={{ color: meta.color, borderColor: meta.color }}>
                    <StatusIcon className="w-3 h-3" /> {meta.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-[#99CCFF] text-center mt-6 flex items-center justify-center gap-1">
          <ExternalLink className="w-3 h-3" /> Distribution typically takes 3–7 business days per platform.
        </p>
      </main>
    </div>
  );
}
